/* eslint-disable complexity */
import { DatePicker, Form, FormInstance } from 'antd';
import dayjs from 'dayjs';
import React, { FC, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AiOutlineEnvironment } from 'react-icons/ai';
import styled from 'styled-components';

import Dropdown from '@/atoms/dropdown';
import { getCurrencySymbol } from '@/utils/currency';
import { BookingCost, DaypassOffices, Location } from '@core/booking/booking.types';

import MultiDateCalendar from './MultiDateCalendar';

const StyledRangePicker = styled.div`
  .ant-picker {
    width: 100%;
    border-radius: 12px;
    height: 44px;
  }
`;

export interface DayPassInfoState {
  quantity: number;
  dates: string[];
  startDate: string | null;
  endDate: string | null;
}

interface BookingCardProps {
  form: FormInstance;
  locations: Location[];
  selectedLocation: Location | null;
  selectedLocationId: string | null;
  setSelectedLocationId: (id: string) => void;
  isReserved: boolean;
  dayPassInfo: DayPassInfoState;
  onToggleDate: (date: string) => void;
  onRemoveDate: (date: string) => void;
  onRangeChange: (startDate: string | null, endDate: string | null) => void;
  allowedOffices: DaypassOffices[];
  selectedUnitId: string | null;
  setSelectedUnitId: (id: string) => void;
  bookingCost: BookingCost | null;
  dayCount: number;
  perDayPrice: string;
  onContinue: () => void;
  canContinue: boolean;
}

/**
 * The sticky booking widget: location, dates, live cost, checkout CTA.
 * Mirrors the flexspace.ai booking card; on mobile it renders inline and
 * the sticky bottom bar (owned by ConfigureStep) carries the CTA instead.
 */
const BookingCard: FC<BookingCardProps> = ({
  form,
  locations,
  selectedLocation,
  selectedLocationId,
  setSelectedLocationId,
  isReserved,
  dayPassInfo,
  onToggleDate,
  onRemoveDate,
  onRangeChange,
  allowedOffices,
  selectedUnitId,
  setSelectedUnitId,
  bookingCost,
  dayCount,
  perDayPrice,
  onContinue,
  canContinue,
}) => {
  const { t } = useTranslation();
  const currencySymbol = getCurrencySymbol(undefined, selectedLocation);
  const hasCost = Number(bookingCost?.total) > 0;
  const vatAmount = hasCost ? (bookingCost!.total - bookingCost!.subTotal).toFixed(2) : '0.00';

  // Reserved mode: the hook clears selectedUnitId whenever the date range or
  // location changes (offices are refetched). The form-controlled office
  // Dropdown would otherwise keep displaying the stale office while the CTA
  // is disabled — sync the form field so the field visibly empties and its
  // required rule re-prompts.
  useEffect(() => {
    if (isReserved) {
      form.setFieldValue('office', selectedUnitId || undefined);
    }
  }, [isReserved, selectedUnitId, form]);

  // Reserved mode: bookable range starts after the selected office's valid_date.
  const earliestDate = useMemo(() => {
    if (isReserved && selectedUnitId) {
      const office = allowedOffices.find((o) => o.id === selectedUnitId);
      if (office) return dayjs(office.valid_date).endOf('day');
    }
    return dayjs().endOf('day');
  }, [isReserved, selectedUnitId, allowedOffices]);

  return (
    <div
      className="w-full bg-white rounded-2xl p-5"
      style={{
        border: '1px solid #E5E7EB',
        boxShadow: '0 1px 3px rgba(16,24,40,0.06), 0 1px 2px rgba(16,24,40,0.04)',
      }}
    >
      {/* Price anchor — only when the tenant exposes a per-day price */}
      {perDayPrice && (
        <>
          <div className="flex items-baseline gap-1">
            <span className="text-2xl font-bold" style={{ color: '#404D61' }} dir="ltr">
              {currencySymbol}
              {perDayPrice}
            </span>
            <span className="text-sm" style={{ color: '#707070' }}>
              {`/ ${t('dayPass.perDay')}`}
            </span>
          </div>
          <div className="my-4" style={{ borderTop: '1px solid #E5E7EB' }} />
        </>
      )}

      <Form form={form} layout="vertical">
        {/* Location */}
        {locations.length === 1 ? (
          <div className="flex items-center gap-2 text-sm mb-4">
            <AiOutlineEnvironment size={16} style={{ color: 'var(--main)', flexShrink: 0 }} />
            <span className="font-medium" style={{ color: '#404D61' }}>
              {locations[0].name}
            </span>
            <span style={{ color: '#707070' }}>{`· ${locations[0].address}`}</span>
          </div>
        ) : (
          <Form.Item
            name="location"
            label={t('dayPass.location')}
            rules={[{ required: true, message: t('dayPass.validation.locationRequired') }]}
            initialValue={selectedLocationId}
            style={{ marginBottom: 16 }}
          >
            <Dropdown
              allowClear={false}
              onChange={(id) => setSelectedLocationId(id)}
              name="Location"
              options={locations.map((loc) => ({
                label: loc.daypass_price
                  ? `${loc.name} — ${getCurrencySymbol(undefined, loc)}${loc.daypass_price}/${t('dayPass.perDay')}`
                  : loc.name,
                value: loc.id,
              }))}
            />
          </Form.Item>
        )}

        {/* Date selection */}
        {isReserved ? (
          <>
            <Form.Item
              name="dateRange"
              label={t('dayPass.datesLabel')}
              rules={[{ required: true, message: t('dayPass.validation.dateRequired') }]}
              style={{ marginBottom: 4 }}
            >
              <StyledRangePicker>
                <DatePicker.RangePicker
                  value={
                    dayPassInfo.startDate && dayPassInfo.endDate
                      ? [dayjs(dayPassInfo.startDate), dayjs(dayPassInfo.endDate)]
                      : null
                  }
                  disabledDate={(current) => current && current < earliestDate}
                  onChange={(range) => {
                    const start = range?.[0] ? range[0].format('YYYY-MM-DD') : null;
                    const end = range?.[1] ? range[1].format('YYYY-MM-DD') : null;
                    onRangeChange(start, end);
                    form.setFieldValue('dateRange', range);
                  }}
                  placeholder={[t('dayPass.startDate'), t('dayPass.endDate')]}
                />
              </StyledRangePicker>
            </Form.Item>
            {dayPassInfo.startDate && dayPassInfo.endDate && (
              <p className="text-xs m-0 mb-3" style={{ color: '#707070' }}>
                {t('dayPass.daysCount', { count: dayCount })}
              </p>
            )}
            <Form.Item
              name="office"
              label={t('dayPass.office')}
              rules={[{ required: true, message: t('dayPass.validation.officeRequired') }]}
              initialValue={selectedUnitId || undefined}
              style={{ marginBottom: 16 }}
            >
              <Dropdown
                disabled={!dayPassInfo.startDate || !dayPassInfo.endDate || !selectedLocationId}
                placeholder={t('dayPass.selectDatesFirst')}
                onChange={(id) => setSelectedUnitId(id)}
                name="Office"
                options={allowedOffices.map((office) => ({
                  label: office.daypass_price
                    ? `${office.name} — ${currencySymbol}${office.daypass_price}/${t('dayPass.perDay')}`
                    : office.name,
                  value: office.id,
                }))}
              />
            </Form.Item>
          </>
        ) : (
          <MultiDateCalendar dates={dayPassInfo.dates} onToggleDate={onToggleDate} onRemoveDate={onRemoveDate} />
        )}
      </Form>

      {/* Live cost summary */}
      {hasCost && (
        <div className="mt-4 pt-4" style={{ borderTop: '1px solid #E5E7EB' }}>
          <div className="flex justify-between text-sm mb-1">
            <span style={{ color: '#707070' }}>
              {perDayPrice
                ? t('dayPass.daysTimesPrice', { days: dayCount, price: `${currencySymbol}${perDayPrice}` })
                : t('meetingRoom.summary.subtotal')}
            </span>
            <span className="font-medium" style={{ color: '#404D61' }} dir="ltr">
              {currencySymbol}
              {bookingCost!.subTotal.toFixed(2)}
            </span>
          </div>
          {bookingCost!.vat > 0 && (
            <div className="flex justify-between text-sm mb-1">
              <span style={{ color: '#707070' }}>{t('meetingRoom.summary.vat', { percent: bookingCost!.vat })}</span>
              <span className="font-medium" style={{ color: '#707070' }} dir="ltr">
                {currencySymbol}
                {vatAmount}
              </span>
            </div>
          )}
          <div className="flex justify-between text-lg font-bold mt-2 pt-2" style={{ borderTop: '1px solid #E5E7EB' }}>
            <span style={{ color: '#404D61' }}>{t('meetingRoom.summary.total')}</span>
            <span style={{ color: 'var(--main)' }} dir="ltr">
              {currencySymbol}
              {bookingCost!.total.toFixed(2)}
            </span>
          </div>
        </div>
      )}

      {/* CTA — desktop only; the mobile sticky bar owns the CTA on small screens */}
      <div className="hidden md:block">
        <button
          type="button"
          onClick={onContinue}
          disabled={!canContinue}
          className="w-full py-3.5 rounded-xl text-base font-semibold border-none cursor-pointer mt-4 transition-opacity"
          style={{
            backgroundColor: canContinue ? 'var(--main)' : '#E5E7EB',
            color: canContinue ? '#FFFFFF' : '#9CA3AF',
          }}
        >
          {t('dayPass.continueToCheckout')}
        </button>
        <p className="text-xs text-center m-0 mt-3" style={{ color: '#707070' }}>
          {t('dayPass.noChargeYet')}
        </p>
      </div>
    </div>
  );
};

export default BookingCard;
